import { BaseError } from './base-error';
import { AuthError } from './auth-error';
import { NetworkError } from './network-error';
import { ErrorCode, ErrorCategory, ErrorSeverity } from './types';
import type { ErrorContext, ErrorResponse } from './types';

/**
 * Global error handler configuration
 */
export interface ErrorHandlerOptions {
  /** Whether to log errors to console */
  logErrors?: boolean;
  /** Whether to include stack traces in development */
  includeStack?: boolean;
  /** Custom error logger function */
  logger?: (error: BaseError) => void | Promise<void>;
  /** Custom telemetry function */
  telemetry?: (error: BaseError) => void | Promise<void>;
  /** Environment (development/production) */
  environment?: string;
}

/**
 * Global error handler for consistent error processing
 */
export class ErrorHandler {
  private options: Required<ErrorHandlerOptions>;

  constructor(options: ErrorHandlerOptions = {}) {
    this.options = {
      logErrors: options.logErrors ?? true,
      includeStack:
        options.includeStack ?? process.env.NODE_ENV === 'development',
      logger: options.logger ?? this.defaultLogger,
      telemetry: options.telemetry ?? (() => {}),
      environment: options.environment ?? process.env.NODE_ENV ?? 'development',
    };
  }

  /**
   * Handle an error and return structured response
   */
  async handle(
    error: unknown,
    context?: ErrorContext
  ): Promise<{
    error: BaseError;
    response: ErrorResponse;
    statusCode: number;
  }> {
    const structuredError = this.normalizeError(error, context);

    // Log the error
    if (this.options.logErrors) {
      await this.options.logger(structuredError);
    }

    // Send to telemetry
    try {
      await this.options.telemetry(structuredError);
    } catch (telemetryError) {
      console.error('Failed to send error to telemetry:', telemetryError);
    }

    return {
      error: structuredError,
      response: structuredError.toResponse(),
      statusCode: structuredError.getHttpStatusCode(),
    };
  }

  /**
   * Normalize any error to a BaseError instance
   */
  normalizeError(error: unknown, context?: ErrorContext): BaseError {
    if (BaseError.isBaseError(error)) {
      return error;
    }

    if (error instanceof Error) {
      // Try to detect error type from message patterns
      if (AuthError.isAuthRelated(error.message)) {
        return AuthError.fromMessage(error.message, context, error);
      }

      // Create generic error
      return new BaseError({
        code: ErrorCode.INTERNAL_UNEXPECTED_ERROR,
        message: error.message,
        category: ErrorCategory.Internal,
        severity: ErrorSeverity.Error,
        context,
        cause: error,
      });
    }

    // Handle non-Error objects
    const message =
      typeof error === 'string' ? error : `Unexpected error: ${String(error)}`;

    return new BaseError({
      code: ErrorCode.INTERNAL_UNEXPECTED_ERROR,
      message,
      category: ErrorCategory.Internal,
      severity: ErrorSeverity.Error,
      context,
    });
  }

  /**
   * Default error logger
   */
  private defaultLogger = (error: BaseError): void => {
    const logData = {
      timestamp: new Date().toISOString(),
      ...error.toJSON(),
    };

    if (error.severity === ErrorSeverity.Fatal) {
      console.error('FATAL ERROR:', logData);
    } else if (error.severity === ErrorSeverity.Error) {
      console.error('ERROR:', logData);
    } else if (error.severity === ErrorSeverity.Warning) {
      console.warn('WARNING:', logData);
    } else {
      console.info('INFO:', logData);
    }
  };

  /**
   * Check if an error is retryable
   */
  isRetryable(error: BaseError): boolean {
    return error.isRetryable();
  }

  /**
   * Get retry delay for retryable errors
   */
  getRetryDelay(error: BaseError, attempt: number): number {
    if (!this.isRetryable(error)) {
      return 0;
    }

    // Exponential backoff with jitter
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);

    // Add jitter (±25%)
    const jitter = delay * 0.25 * (Math.random() * 2 - 1);
    return Math.round(delay + jitter);
  }

  /**
   * Create error from HTTP response
   */
  static async fromHttpResponse(
    response: Response,
    context?: ErrorContext
  ): Promise<BaseError> {
    if (response.ok) {
      throw new Error('Response is not an error response');
    }

    // Handle authentication errors
    if (response.status === 401) {
      return AuthError.invalidToken(context);
    }

    if (response.status === 403) {
      return AuthError.insufficientPermissions(context);
    }

    // Handle other status codes with network error
    return NetworkError.fromResponse(response, context);
  }
}

/**
 * Default global error handler instance
 */
export const globalErrorHandler = new ErrorHandler();

/**
 * Convenience function for handling errors
 */
export async function handleError(
  error: unknown,
  context?: ErrorContext
): Promise<{
  error: BaseError;
  response: ErrorResponse;
  statusCode: number;
}> {
  return globalErrorHandler.handle(error, context);
}

/**
 * Convenience function for normalizing errors
 */
export function normalizeError(
  error: unknown,
  context?: ErrorContext
): BaseError {
  return globalErrorHandler.normalizeError(error, context);
}
